import {
  MasterpassAccountAccessRequest,
  MasterpassAddCardRequest,
  MasterpassBillDetails,
  MasterpassBuyerDetails,
  MasterpassDeliveryDetails,
  MasterpassLinkToMerchantRequest,
  MasterpassOrderDetails,
  MasterpassOrderProductsDetails,
  MasterpassOtherDetails,
  MasterpassPaymentRequest,
  MasterpassRecurringOrderRequest,
  MasterpassRemoveCardRequest,
  MasterpassVerifyOtpRequest,
} from 'Models/MasterpassSdkModels';
import { getPublicIpFromStorage } from 'Utils';

/**
 * Masterpass SDK v1.0.7 istek gövdeleri.
 *
 * ⚠️ SDK, `Utils.operationParameters()` ile her isteği bir whitelist'ten geçirir ve
 * listede olmayan alanları GÖNDERMEDEN ÖNCE SESSİZCE SİLER. Bu yüzden eski MFS
 * gövdelerindeki `uiChannelType`, `timeZone`, `sendSms`, `sendSmsLanguage`,
 * `mobileAccountConfig`, `identityVerificationFlag`, `defaultAccount`,
 * `cardTypeFlag`, `eActionType`, `actionType`, `clientId`, `clientType`,
 * `version`, `dateTime`, `fp`, `aav`, `encCPin`, `encPassword`, `password`,
 * `clientIp` alanlarının hiçbiri buraya taşınmadı — taşınsaydı da gitmezlerdi.
 *
 * Kimlik alanları (`accountKey`, `userId`) burada DOLDURULMAZ: JWT'den gelirler ve
 * saga katmanında `authorizeMasterpassSdk()` sonrası eklenirler. Böylece telefon
 * numarası tek bir kaynaktan (JWT) beslenir.
 */

/**
 * Belbim sanal pos sipariş kaleminin sabit tanımı — tek kalemli tek ürün.
 * Hem ödemede (`orderProductsDetails`) hem kart eklemede (`additionalParams`)
 * kullanılır; iki akış aynı ürün kodunu bildirmelidir.
 */
const BELBIM_ORDER_PRODUCT = {
  name: 'Belbim Sanal Pos Ödemesi',
  code: 'BELBIM1',
  info: 'VPOSPURCHASE',
  priceType: 'VIRTUAL',
  qty: '1',
};

/* ------------------------------------------------------------------ *
 * Kart ekleme
 * ------------------------------------------------------------------ */

/**
 * Masterpass hesabına kart ekleme gövdesi (`accountService.addCard`).
 *
 * ⚠️ Alan adı değişiklikleri: `rtaPan` → `cardNumber`, `cvc` → `cvv`.
 * (`cvv` sadece kart ekleme içindir; ödeme servisleri `cvc` kullanır.)
 *
 * ⚠️ `email` parametresi korunuyor ancak SDK'nın kart ekleme whitelist'inde
 * e-posta alanı YOKTUR; gerekiyorsa `additionalParams.Email` üzerinden gönderilir.
 *
 * ⚠️ Kart ekleme whitelist'i ödeme servislerinden ÇOK DAR:
 * `["accountKey","accountKeyType","userId","requestReferenceNumber","cardNumber",
 *   "expiryDate","accountAliasName","cardHolderName","cvv","deviceFingerPrint",
 *   "additionalParams"]`
 * Yani `orderProductsDetails` ve diğer yapılandırılmış bloklar burada GÖNDERİLEMEZ;
 * sipariş bilgisi için tek kanal `additionalParams`'tır. Bkz.
 * `masterPassAddCardOrderParamsNormalize`.
 *
 * @param expiryDate `YYAA` formatında olmalıdır (örn. `2612` = Aralık 2026).
 */
export const masterPassRegisterNormalize = (
  email: string,
  pan: string,
  expiryDate: string,
  cvv: string,
  cardName: string,
  cardHolderName?: string,
  requestReferenceNumber?: string
): Omit<MasterpassAddCardRequest, 'accountKey' | 'accountKeyType'> => ({
  cardNumber: pan,
  expiryDate,
  cvv,
  accountAliasName: cardName,
  cardHolderName,
  requestReferenceNumber,
  additionalParams: {
    ...masterPassAddCardOrderParamsNormalize(),
    ...(email ? { Email: email } : {}),
  },
});

/**
 * Kart ekleme isteğinin `additionalParams` bloğuna konan sipariş kalemi bilgisi.
 *
 * ⚠️ Neden gerekli: kart ekleme, üye işyeri yapılandırması `_3D` olduğu için
 * Masterpass tarafında `registration/authorize` → `Vpos/auth-3d-secure` zincirini
 * tetikliyor; VPos ise sipariş kalemi kodu olmadan isteği
 * `ORDER_PRODUCT_CODE_NOT_VALID` ("OrderProductCode is empty.") ile reddediyor.
 * Eski MFS `register` çağrısı bu zincirden geçmediği için böyle bir alan istemiyordu.
 *
 * ⚠️⚠️ BU YÖNTEM ÇALIŞMIYOR — ÇÖZÜM BEKLİYOR.
 * Alan adları çıkarımdı (whitelist kart eklemede yalnızca `additionalParams`'a
 * izin veriyor, SDK bu nesneyi dönüştürmeden geçiriyor ve servisin doğrulama
 * hataları alan adlarını PascalCase veriyor). Bir süre "kabul edildi" sanıldı,
 * çünkü o denemede istek `CARD_ALREADY_EXISTS` ile ERKEN döndü ve VPos adımına
 * hiç ulaşmadı. Yeni bir kartla denendiğinde gerçek cevap çıktı:
 *
 *   İstek : additionalParams.OrderProductCode = "BELBIM1"  (gönderiliyor)
 *   Cevap : 400 ORDER_PRODUCT_CODE_NOT_VALID — "OrderProductCode is empty."
 *           details: registration/authorize → Vpos/auth-3d-secure
 *
 * Yani sipariş kalemi kart eklemede `additionalParams` üzerinden geçmiyor ve
 * SDK'nın kart ekleme whitelist'inde `orderProductsDetails` YOK. Doğru kanalın
 * ne olduğu Masterpass'e soruldu; cevap gelene kadar bu blok gönderilmeye devam
 * ediyor (zararı yok, yok sayılıyor).
 *
 * ⚠️ Ödemedeki `orderProductsDetails` bloğunun aksine burada değerler DİZİ DEĞİL
 * düz string: `additionalParams` mobil SDK'larda `[String: String]` olarak
 * tiplenmiş, yani düz bir sözlük.
 *
 * ⚠️ Fiyat alanı BİLEREK yok: kart ekleme bir sipariş değil, kart doğrulama
 * yetkilendirmesi; tutarı üye işyeri yapılandırması belirliyor. Servis fiyat
 * isterse hata mesajında alan adını verecektir.
 */
export const masterPassAddCardOrderParamsNormalize = (): Record<string, string> => ({
  OrderProductCode: BELBIM_ORDER_PRODUCT.code,
  OrderProductName: BELBIM_ORDER_PRODUCT.name,
  OrderProductInfo: BELBIM_ORDER_PRODUCT.info,
  OrderPriceType: BELBIM_ORDER_PRODUCT.priceType,
  OrderQty: BELBIM_ORDER_PRODUCT.qty,
});

/* ------------------------------------------------------------------ *
 * Kart listeleme / hesap erişimi
 * ------------------------------------------------------------------ */

/**
 * Kayıtlı kartları listeleme gövdesi (`accountService.accountAccess`).
 *
 * Eski `MFS.listCards(msisdn, token, cb)` çağrısının yerini alır. `accountKey`
 * (telefon) ve `userId` JWT'den geldiği için burada boş bırakılır.
 */
export const masterPassListCardNormalize = (): Pick<MasterpassAccountAccessRequest, 'accountKeyType'> => ({
  accountKeyType: 'Msisdn',
});

/* ------------------------------------------------------------------ *
 * Doğrulama
 * ------------------------------------------------------------------ */

/** Doğrulama adımının tipi — hangi alan adıyla gönderileceğini belirler. */
export type MasterpassVerificationStep = 'otp' | 'validation';

/**
 * OTP / CVV / RTA doğrulama gövdesi (`verifyService.verifyOtp`).
 *
 * ⚠️ Alan adı adıma göre değişir: SMS kodu `otpCode`, CVV ve RTA (tutar)
 * doğrulaması ise `validationCode` alanıyla gönderilir. Eski SDK her ikisi için
 * de `validationCode` kullanıyordu.
 */
export const masterPassOtpNormalize = (
  code: string,
  step: MasterpassVerificationStep = 'otp'
): MasterpassVerifyOtpRequest => (step === 'otp' ? { otpCode: code } : { validationCode: code });

/* ------------------------------------------------------------------ *
 * Ödeme
 * ------------------------------------------------------------------ */

export type MasterpassPaymentDetailsParams = {
  /** Kuruş cinsinden tutar — istekteki `amount` ile AYNI değer olmalıdır. */
  amount: number | string;
  name?: string;
  surname?: string;
  email?: string;
  phoneNumber?: string;
  /** `YYYY-MM-DD HH:mm:ss` — alıcı geçmişi ve istemci zamanı alanları için. */
  formattedDate: string;
};

export type MasterpassPaymentDetails = {
  orderDetails: MasterpassOrderDetails;
  orderProductsDetails: MasterpassOrderProductsDetails;
  buyerDetails: MasterpassBuyerDetails;
  billDetails: MasterpassBillDetails;
  deliveryDetails: MasterpassDeliveryDetails;
  otherDetails: MasterpassOtherDetails;
};

/**
 * Ödeme isteğinin sipariş / fatura / teslimat / alıcı bloklarını üretir.
 *
 * ⚠️ Eski akış bu bilginin tamamını `additionalParams` içinde snake_case
 * (`order_product_code_arr`, `bill_first_name`, ...) gönderiyordu. Yeni servis
 * o gövdeyi OKUMUYOR; sipariş kalemlerini `orderProductsDetails` içinden
 * bekliyor ve kalem kodu bulamayınca isteği `ORDER_PRODUCT_CODE_NOT_VALID` /
 * "OrderProductCode is empty." ile 400 döndürüyor.
 *
 * ⚠️ `orderProductsDetails` alanları PARALEL DİZİDİR; Belbim tarafında tek kalem
 * olduğu için hepsi tek elemanlıdır ve `OrderPrice` isteğin `amount` değeriyle
 * birebir aynı olmalıdır.
 *
 * ⚠️ Eski gövdedeki `card_program_name: 'MASTERCARD'` alanı BİLEREK taşınmadı:
 * değer sabitti ve kart şeması Visa/Troy olduğunda yanlış bilgi veriyordu.
 * Artık bu blok servise gerçekten ulaştığı için yanlış bir değeri beyan etmek
 * yerine alan hiç gönderilmiyor. Gerekirse BIN listesinden türetilmelidir.
 * Aynı gerekçeyle karşılığı olmayan eski `number_time` / `owner_time` alanları
 * da taşınmadı.
 */
export const masterPassPaymentDetailsNormalize = ({
  amount,
  name,
  surname,
  email,
  phoneNumber,
  formattedDate,
}: MasterpassPaymentDetailsParams): MasterpassPaymentDetails => {
  const buyerName = name?.trim() ? name : 'fname';
  const buyerSurname = surname?.trim() ? surname : 'lname';
  const buyerEmail = email?.trim() ? email : 'test@test.com';
  const buyerPhone = phoneNumber?.trim() ? phoneNumber : '901111111111';
  // Public IP henüz alınmamış olabilir; alan boş string yerine hiç gönderilmez.
  const clientIp = getPublicIpFromStorage() ?? undefined;

  return {
    orderProductsDetails: {
      OrderProductName: [BELBIM_ORDER_PRODUCT.name],
      OrderProductCode: [BELBIM_ORDER_PRODUCT.code],
      OrderProductInfo: [BELBIM_ORDER_PRODUCT.info],
      OrderPriceType: [BELBIM_ORDER_PRODUCT.priceType],
      OrderQty: [BELBIM_ORDER_PRODUCT.qty],
      OrderPrice: [String(amount)],
    },
    orderDetails: {
      Shipping: '0',
      Timeout: '123',
      BasketId: '123456',
      VendorId: '123456',
      CommisionRate: 0,
      PaymentContent: 'istanbulkart',
      TransactionDescription: 'Belbim Sanal Pos Ödemesi',
    },
    buyerDetails: {
      BuyerId: '123456',
      IdentityNumber: '123456',
      LastLoginDate: formattedDate,
      RegistrationDate: formattedDate,
      Name: `${buyerName} ${buyerSurname}`,
      Email: buyerEmail,
      Phone: buyerPhone,
      Ip: clientIp,
    },
    billDetails: {
      BillFirstName: buyerName,
      BillLastName: buyerSurname,
      BillEmail: buyerEmail,
      BillPhone: buyerPhone,
      BillCountryCode: 'TR',
      BillFax: '123213123',
      BillAddress: 'bill_address',
      BillAddress2: 'bill_address2',
      BillZipCode: '124214',
      BillCity: 'ISTANBUL',
      BillState: 'bill_state',
    },
    deliveryDetails: {
      DeliveryFirstName: buyerName,
      DeliveryLastName: buyerSurname,
      DeliveryEmail: buyerEmail,
      DeliveryPhone: buyerPhone,
      DeliveryCompany: 'company',
      DeliveryAddress: 'deliveryaddres',
      DeliveryAddress2: 'deliveryaddress2',
      DeliveryZipCode: '325325',
      DeliveryCity: 'deliverycity',
      DeliveryState: 'deliverystate',
      DeliveryCountryCode: 'TR',
    },
    otherDetails: {
      ClientIp: clientIp,
      ClientTime: formattedDate,
    },
  };
};

/**
 * Kayıtlı kartla ödeme gövdesi (`paymentService.payment`).
 *
 * ⚠️ Kart artık maskeli PAN (`rtaPan`) ile değil **kart adı** (`cardAlias`) ile
 * seçilir — çağıranlar kartın `Name` alanını değil `cardAlias` alanını vermeli.
 *
 * ⚠️ `macroMerchantId`'nin yeni SDK'da doğrudan karşılığı YOK: ödeme whitelist'i
 * `subMerchant` / `terminalGroupId` / `acquirerIcaNumber` alanlarını tanıyor.
 * Buraya `subMerchant` olarak eşlendi; Masterpass entegrasyon dokümanıyla
 * DOĞRULANMALI, yanlış eşleme işlemin yanlış alt üye işyerine düşmesine yol açar.
 *
 * @param loadAmount TL cinsinden tutar; Masterpass kuruş beklediği için 100 ile çarpılır.
 */
export const masterPassPaymentNormalize = (
  loadAmount: number,
  cardAlias: string,
  macroMerchantIdParam?: string
): Omit<MasterpassPaymentRequest, 'accountKey'> => ({
  amount: Number((loadAmount * 100).toFixed(2)),
  cardAlias,
  ...(macroMerchantIdParam ? { subMerchant: macroMerchantIdParam } : {}),
});

/* ------------------------------------------------------------------ *
 * Kart silme / hesap bağlama
 * ------------------------------------------------------------------ */

/**
 * Kart silme gövdesi (`accountService.removeCard`).
 * ⚠️ Eski gövdedeki sabit `referenceNo: '101252836185'` değeri kaldırıldı —
 * yeni serviste böyle bir alan yok.
 */
export const masterPassDeleteCardNormalize = (cardAlias: string): Omit<MasterpassRemoveCardRequest, 'accountKey'> => ({
  cardAlias,
});

/**
 * Masterpass hesabını üye işyerine bağlama gövdesi (`accountService.linkToMerchant`).
 * Servis yalnızca `accountKey` + `userId` alır; ikisi de JWT'den geldiği için gövde boştur.
 */
export const masterPassLinkToClientNormalize = (): Partial<MasterpassLinkToMerchantRequest> => ({});

/* ------------------------------------------------------------------ *
 * Otomatik talimat
 * ------------------------------------------------------------------ */

/**
 * Otomatik talimat gövdesi (`accountService.recurringOrder` / `...Update` / `...Delete`).
 *
 * ⚠️ Eski `initiateRecurringPayment` tek metottu ve işlemi `actionType` (`'A'`/`'D'`)
 * belirliyordu. Yeni SDK'da her işlem AYRI METOT olduğu için `actionType` parametresi
 * kaldırıldı; ekleme/güncelleme/silme seçimi çağıran tarafta yapılır.
 *
 * ⚠️ `transactionFee` yeni whitelist'te YOK — komisyon bilgisi bu gövdeyle iletilemez.
 *
 * @param endDate Talimatın bitiş tarihi; `expiryDate` alanına yazılır.
 */
export const masterPassRecurringPaymentNormalize = (
  cardAlias: string,
  amountLimit: string,
  endDate: string,
  productId: string,
  requestReferenceNumber: string
): Omit<MasterpassRecurringOrderRequest, 'accountKey'> => ({
  cardAlias,
  amountLimit,
  // ⚠️ Boş alanlar GÖNDERİLMEZ. `expiryDate` (talimat bitiş tarihi) ve
  // `requestReferenceNumber` opsiyonel; boş string göndermek servisin bunları
  // "verilmiş ama geçersiz" saymasına yol açabilir. Referans verilmezse saga
  // kendisi üretir.
  ...(endDate ? { expiryDate: endDate } : {}),
  ...(productId ? { productId } : {}),
  ...(requestReferenceNumber ? { requestReferenceNumber } : {}),
});
